/**
 * Variable-creation reads for the static adapter.
 *
 * Sixteen are backed by fixtures. One — `listSpend` — still throws
 * `NotImplementedError`, deliberately: spend arrives by upload until activation
 * management and contract management serve it (DEC-12), and the read stays as the shape
 * that integration must satisfy. A screen calling it fails loudly and names the method,
 * rather than rendering an empty list that reads as "there is no spend".
 *
 * Wiring it means moving it out of the throwing group and deleting its name from
 * `STILL_STUBBED` in `tests/data/mmmStubs.test.ts`. That second edit is not
 * bureaucracy: it makes "we wired a read" a visible line in a diff.
 *
 * Everything here is INVENTED data on the REAL shapes. The activation rows, report
 * register and per-year counts are generated deterministically by
 * `@/domain/mmm/sourceRecords` — same key, same row, on every machine — which is what
 * lets the inventory's coverage numbers and its openable lists agree.
 */

import {
  activityCounts,
  buildActivities,
  buildReports,
  type ActivityInput,
  type ReportInput,
} from '@/domain/mmm/sourceRecords';
import type {
  Id,
  MmmActivity,
  MmmActivityQuery,
  MmmActivityYearCount,
  MmmContractEntitlements,
  MmmContractRecord,
  MmmCountry,
  MmmDqsAgent,
  MmmDqsResult,
  MmmEntitlement,
  MmmGeoNode,
  MmmMetric,
  MmmRateCard,
  MmmRateCardRevision,
  MmmReport,
  MmmScopeQuery,
  MmmSpend,
  MmmSpendBasis,
  MmmSpendFile,
  MmmSpendTemplate,
  MmmSpendUpload,
  MmmVisibilityChannel,
} from '../contracts';
import { NotImplementedError } from '../ports';
import type { MmmPort } from '../ports';
import {
  VC_ARCHETYPE_CHANNELS,
  VC_CHANNELS,
  VC_CONTRACTS,
  VC_COUNTRIES,
  VC_DQS_ASSESSMENTS,
  VC_ENTITLEMENTS,
  VC_GEO_NODES,
  VC_METRICS,
  VC_RATE_CARD_REVISION,
  VC_RATE_CARD_VERSION,
  VC_SPEND_TEMPLATE,
} from './fixtures/variableCreation';

type VariableCreationReads = Pick<
  MmmPort,
  | 'listContractRecords'
  | 'listCountries'
  | 'listGeoChildren'
  | 'readGeoNodes'
  | 'listVisibilityChannels'
  | 'listMetricDefinitions'
  | 'listEntitlementDefinitions'
  | 'readEntitlements'
  | 'listActivities'
  | 'countActivitiesByYear'
  | 'listReports'
  | 'listSpend'
  | 'readSpendTemplate'
  | 'parseSpendUpload'
  | 'findDqsResults'
  | 'readRateCard'
  | 'writeRateCard'
>;

/** Resolves on a microtask, mirroring the shape of a real request. */
const ok = <T>(value: T): Promise<T> => Promise.resolve(value);

/** The contracts a scope query names, in register order. Unknown ids are dropped, not invented. */
const contractsIn = (ids: readonly Id[]): readonly MmmContractRecord[] => {
  const set = new Set(ids);
  return VC_CONTRACTS.filter((c) => set.has(c.id));
};

/**
 * The years the analysis window touches (DEC-11). Derived here, on the server side of
 * the port, so no caller can enumerate a different set.
 */
export function yearsOf(query: MmmScopeQuery): readonly string[] {
  const from = Number(query.from.slice(0, 4));
  const to = Number(query.to.slice(0, 4));
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return [];
  const out: string[] = [];
  for (let y = from; y <= to && out.length < 40; y++) out.push(String(y));
  return out;
}

const channelsForArchetype = (contract: MmmContractRecord): readonly Id[] =>
  VC_ARCHETYPE_CHANNELS[contract.archetype];

const activityInput = (query: MmmScopeQuery): ActivityInput => {
  const contracts = contractsIn(query.contractIds);
  const byId = new Map(contracts.map((c) => [c.id, c]));
  return {
    contracts: contracts.map((c) => ({
      id: c.id,
      name: c.contract.name,
      rightsHolder: c.rightsHolder,
      brand: c.masterbrand,
      geo: c.market === '' ? '(country)' : c.market,
    })),
    years: yearsOf(query),
    channelsFor: (id) => {
      const c = byId.get(id);
      return c === undefined
        ? []
        : channelsForArchetype(c).map((ch) => VC_CHANNELS.find((x) => x.id === ch)?.name ?? ch);
    },
  };
};

const reportInput = (query: MmmScopeQuery): ReportInput => ({
  contracts: contractsIn(query.contractIds).map((c) => ({
    id: c.id,
    name: c.contract.name,
    rightsHolder: c.rightsHolder,
    brand: c.masterbrand,
  })),
  years: yearsOf(query),
});

/* ------------------------------------------------------------- spend parsing -- */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** RFC 4180-enough: quoted cells, doubled quotes, comma or semicolon delimiter. */
function splitLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line.charAt(i);
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else quoted = false;
      } else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

/**
 * Parse an uploaded spend file. Every row carries the DECLARED basis; a row that cannot
 * be trusted is returned in `rejected` with its line number and reason, never dropped.
 */
export function parseSpendText(
  text: string,
  basis: MmmSpendBasis,
  knownContracts: ReadonlySet<Id> = new Set(VC_CONTRACTS.map((c) => c.id)),
): MmmSpendUpload {
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
  const headerLine = lines.find((l) => l.trim() !== '') ?? '';
  const delimiter = headerLine.includes(';') && !headerLine.includes(',') ? ';' : ',';
  const header = splitLine(headerLine, delimiter).map((h) => h.toLowerCase());
  const col = (key: string): number => header.indexOf(key);
  const required = ['contract_id', 'entitlement_id', 'start_date', 'end_date', 'amount'];
  const missing = required.filter((k) => col(k) < 0);

  const rows: MmmSpend[] = [];
  const rejected: { line: number; reason: string; raw: string }[] = [];
  if (missing.length) {
    rejected.push({
      line: 1,
      reason: `Missing required column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`,
      raw: headerLine,
    });
    return { basis, currency: 'EUR', rows, rejected };
  }

  let currency = '';
  let started = false;
  lines.forEach((raw, i) => {
    if (raw.trim() === '') return;
    if (!started) {
      // The first non-empty line is the header.
      started = true;
      return;
    }
    const cells = splitLine(raw, delimiter);
    const at = (key: string): string => cells[col(key)] ?? '';
    const contractId = at('contract_id');
    const entitlementId = at('entitlement_id');
    const startDate = at('start_date');
    const endDate = at('end_date');
    const amountText = at('amount').replace(/\s/g, '');
    const amount = Number(amountText);
    const rowCurrency = col('currency') >= 0 ? at('currency').toUpperCase() : '';
    const line = i + 1;

    if (contractId === '' || !knownContracts.has(contractId)) {
      rejected.push({ line, reason: `Unknown contract id "${contractId}"`, raw });
      return;
    }
    if (entitlementId === '') {
      rejected.push({ line, reason: 'Entitlement id is empty', raw });
      return;
    }
    if (!ISO_DATE.test(startDate) || !ISO_DATE.test(endDate)) {
      rejected.push({ line, reason: 'Dates must be ISO (YYYY-MM-DD)', raw });
      return;
    }
    if (endDate < startDate) {
      rejected.push({ line, reason: 'End date is before start date', raw });
      return;
    }
    if (amountText === '' || !Number.isFinite(amount)) {
      rejected.push({ line, reason: `Amount "${at('amount')}" is not a number`, raw });
      return;
    }
    if (amount < 0) {
      rejected.push({ line, reason: 'Amount is negative', raw });
      return;
    }
    if (currency === '') currency = rowCurrency || 'EUR';
    else if (rowCurrency !== '' && rowCurrency !== currency) {
      rejected.push({
        line,
        reason: `Currency ${rowCurrency} differs from ${currency} — one currency per file`,
        raw,
      });
      return;
    }
    rows.push({
      basis,
      contractId,
      entitlementId,
      startDate,
      endDate,
      amount,
      currency: rowCurrency || currency,
    });
  });

  return { basis, currency: currency || 'EUR', rows, rejected };
}

/* ------------------------------------------------------------- DQS results -- */

const AGENT_CATALOGUE: readonly MmmDqsAgent[] = [
  'dqs_assessment',
  'data_recommendation',
  'data_qa',
];

/**
 * Joined on the run identifier ALONE. An agent's most recent assessment is never
 * substituted for the one belonging to this run (NEW-28, integration point 9).
 */
export function dqsResultsFor(
  runId: Id,
  agentsInScope: readonly MmmDqsAgent[],
): readonly MmmDqsResult[] {
  return AGENT_CATALOGUE.map((agent): MmmDqsResult => {
    if (!agentsInScope.includes(agent)) return { agent, state: 'not_in_scope' };
    const rec = VC_DQS_ASSESSMENTS.find((r) => r.agent === agent && r.runId === runId);
    // In scope, nothing found: a state, not a zero and not an omission.
    if (rec === undefined) return { agent, state: 'not_run' };
    return {
      agent,
      state: rec.completeness >= 1 ? 'delivered' : 'partial',
      score: rec.score,
      completeness: rec.completeness,
      ranAt: rec.ranAt,
      note: rec.note,
    };
  });
}

/* ------------------------------------------------------------------ reads -- */

/** Spread into the static adapter's `mmm` block. */
export const mmmVariableCreation: VariableCreationReads = {
  /* ---- backed by fixtures ---- */

  listContractRecords: (): Promise<readonly MmmContractRecord[]> => ok(VC_CONTRACTS),

  listCountries: (): Promise<readonly MmmCountry[]> => ok(VC_COUNTRIES),

  listGeoChildren: (parentId: Id): Promise<readonly MmmGeoNode[]> =>
    ok(VC_GEO_NODES.filter((n) => n.parentId === parentId)),

  // Ids in, labels out. The caller stores ids and never a copy of the name.
  readGeoNodes: (ids: readonly Id[]): Promise<readonly MmmGeoNode[]> =>
    ok(VC_GEO_NODES.filter((n) => ids.includes(n.id))),

  listVisibilityChannels: (): Promise<readonly MmmVisibilityChannel[]> => ok(VC_CHANNELS),

  listMetricDefinitions: (): Promise<readonly MmmMetric[]> => ok(VC_METRICS),

  listEntitlementDefinitions: (): Promise<readonly MmmEntitlement[]> => ok(VC_ENTITLEMENTS),

  /**
   * The entitlement pull. PROPOSES channels — one contract reaches many — and the
   * partner map confirms them. The source is the archetype stand-in (NEW-53).
   */
  readEntitlements: (contractIds: readonly Id[]): Promise<readonly MmmContractEntitlements[]> =>
    ok(
      contractsIn(contractIds).map((c) => {
        const channels = channelsForArchetype(c);
        return {
          contractId: c.id,
          entitlementIds: VC_ENTITLEMENTS.filter((e) => channels.includes(e.channel)).map(
            (e) => e.id,
          ),
          proposedChannelIds: [...channels],
        };
      }),
    ),

  listActivities: (query: MmmActivityQuery): Promise<readonly MmmActivity[]> => {
    const rows = buildActivities(activityInput(query));
    const offset = query.offset ?? 0;
    return ok(rows.slice(offset, offset + query.limit));
  },

  // A count query, not `rows.length` — the years come from the window (DEC-11).
  countActivitiesByYear: (query: MmmScopeQuery): Promise<readonly MmmActivityYearCount[]> =>
    ok(activityCounts(activityInput(query))),

  // The report TYPES arrive with the data. `category` is whatever the document system says.
  listReports: (query: MmmScopeQuery): Promise<readonly MmmReport[]> =>
    ok(
      buildReports(reportInput(query)).map((r): MmmReport => ({
        id: r.id,
        title: r.title,
        provider: r.provider,
        category: r.category,
        quality: r.quality,
        access: r.access,
        brand: r.brand,
        rightsHolder: r.rightsHolder,
        date: r.date,
        sizeMb: r.sizeMb,
        teams: r.teams,
        layer: r.layer === 'cogent' ? 'experiential' : r.layer,
      })),
    ),

  readSpendTemplate: (): Promise<MmmSpendTemplate> => ok(VC_SPEND_TEMPLATE),

  parseSpendUpload: (file: MmmSpendFile, basis: MmmSpendBasis): Promise<MmmSpendUpload> =>
    ok(parseSpendText(file.text, basis)),

  findDqsResults: (
    runId: Id,
    agentsInScope: readonly MmmDqsAgent[],
  ): Promise<readonly MmmDqsResult[]> => ok(dqsResultsFor(runId, agentsInScope)),

  readRateCard: (): Promise<MmmRateCardRevision> => ok(VC_RATE_CARD_REVISION),

  /**
   * Echoes rather than persists — the header rule for this whole adapter — but it
   * DOES enforce the version, so the conflict path is reachable from the screens
   * instead of only existing on the server. Present anything but the current version
   * and the write is refused, exactly as the endpoint will refuse it.
   */
  writeRateCard: (card: MmmRateCard, version: string): Promise<MmmRateCardRevision> =>
    version === VC_RATE_CARD_VERSION
      ? ok({ ...VC_RATE_CARD_REVISION, card })
      : Promise.reject(
          new Error(
            `Rate card version "${version}" is stale — the stored card is at ` +
              `"${VC_RATE_CARD_VERSION}". Re-read it and apply your change again.`,
          ),
        ),

  /* ---- no fixture, by decision (DEC-12) ---- */

  listSpend: () => {
    throw new NotImplementedError('mmm.listSpend');
  },
};
